import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { CheckCircleIcon, XCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import ClientInstructionDocuments from './ClientInstructionDocuments';
import ClientInstructionNotes from './ClientInstructionNotes';
import ClientPersonalEmailTab from './ClientPersonalEmailTab';
import ClientInstructionEmailTab from './ClientInstructionEmailTab';
import ClientInstructionWhatsAppTab from './ClientInstructionWhatsAppTab';

interface Client {
  id: number;
  name: string;
  surname: string;
  email: string;
  mobile_number?: string;
  date_of_birth: string;
  client_type: string;
}

interface Policy {
  id: number;
  policy_number: string;
  product_type: string;
  status: string;
}

interface Consultant {
  id: number;
  name: string;
  surname: string;
  email: string;
  role: string;
}

interface Employee {
  id: number;
  name: string;
  surname: string;
  email: string;
  role: string;
}

interface Instruction {
  id: number | string;
  created_date: string;
  reference_number: string;
  client_name: string;
  consultant_name: string;
  employee_name: string;  // ADD THIS
  consultant_surname?: string;
  assigned_to?: number;
  consultant_id?: number;
  instruction_type: string;
  instruction_method?: string;
  description: string;
  status: string;
  policy_number?: string;
  policy_id?: number;
  date_received?: string;
  date_sign_off?: string;
  signed_off_by?: string;
}

interface InstructionDetail extends Instruction {
  instruction_method: string;
  date_received: string;
  date_sign_off: string;
  policy_id?: number;
  client_id: number;
  originalDescription?: string;
  signed_off_by?: string;
}

interface ClientPersonalInstructionTabProps {
  clientId: string;
  status?: string;
  instructionId?: string;
}

const API_URL = process.env.REACT_APP_API_URL || '';

const ClientPersonalInstructionTab: React.FC<ClientPersonalInstructionTabProps> = ({ clientId, instructionId }) => {
  const location = useLocation();
  // View state
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedInstructionId, setSelectedInstructionId] = useState<string | null>(null);
  const [selectedInstruction, setSelectedInstruction] = useState<InstructionDetail | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'documents' | 'notes' | 'whatsapp' | 'email'>('details');
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);
  const [isSavingInstructionType, setIsSavingInstructionType] = useState(false);
  const [isEditingInstructionType, setIsEditingInstructionType] = useState(false);
  const [editingInstructionType, setEditingInstructionType] = useState('');
  const [signedOffByName, setSignedOffByName] = useState<string>('');

  // ✅ ADD THIS - Refresh key for notes tab
const [refreshKey, setRefreshKey] = useState(0);

const refreshNotes = () => {
  console.log('🔄 Refreshing notes...');
  // Dispatch event to refresh notes
  window.dispatchEvent(new CustomEvent('refresh-instruction-notes'));
};
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Instructions list state
  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [isLoadingInstructions, setIsLoadingInstructions] = useState(false);

  // Form state (for modal)
  const [formData, setFormData] = useState({
    policyId: '',
    instructionType: '',
    instructionMethod: '',
    description: '',
    assignedTo: ''
  });

  // Data state
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoadingConsultants, setIsLoadingConsultants] = useState(false);

  // Selected client details
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Static options
  const instructionTypes = [
    { value: 'new_product', label: 'New Product' },
    { value: 'update', label: 'Update' },
    { value: 'cancellation', label: 'Cancellation' }
  ];

  const instructionMethods = [
    { value: 'face_to_face', label: 'Face To Face' },
    { value: 'email', label: 'E-Mail' },
    { value: 'phone_call', label: 'Phone Call' },
    { value: 'whatsapp', label: 'Whatsapp' }
  ];

  const policyStatuses = [
    { value: 'registered', label: 'Registered' },
    { value: 'send_to_client', label: 'Send To Client' },
    { value: 'sent_to_insurer', label: 'Sent To Insurer' },
    { value: 'schedule_sent_to_client', label: 'Schedule Sent To Client' },
    { value: 'completed', label: 'Completed' }
  ];

    const instructionStatuses = [
    { value: 'registered', label: 'Registered' },
    { value: 'send_to_client', label: 'Sent To Client' },
    { value: 'sent_to_insurer', label: 'Sent To Insurer' },
    { value: 'schedule_sent_to_client', label: 'Schedule Sent To Client' },
    { value: 'completed', label: 'Completed' }
  ];

  // Fetch initial data
useEffect(() => {
  fetchClientDetails();
  fetchInstructions();
  fetchEmployees();  // For the modal
  fetchConsultants(); // For the instruction details
}, [clientId]);

  // Check if we need to open a specific instruction
useEffect(() => {
  const state = location.state as { instructionId?: string };
  if (state?.instructionId && !isLoadingInstructions && instructions.length > 0) {
    console.log('🔍 Opening instruction from navigation:', state.instructionId);
    handleReferenceClick(state.instructionId);
    // Clear the state so it doesn't re-open on re-render
    window.history.replaceState({}, document.title);
  }
}, [location.state, isLoadingInstructions, instructions.length]);

// Handle instructionId passed as prop (from Reports navigation)
useEffect(() => {
  if (instructionId && !isLoadingInstructions && instructions.length > 0) {
    console.log('🔍 Opening instruction from prop:', instructionId);
    // Find the instruction in the list
    const instruction = instructions.find(i => i.id.toString() === instructionId);
    if (instruction) {
      handleReferenceClick(instructionId);
    }
  }
}, [instructionId, isLoadingInstructions, instructions.length]);

  const fetchInstructions = async () => {
  setIsLoadingInstructions(true);
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');

    const response = await axios.get(
      `${API_URL}/api/instructions?clientId=${clientId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    console.log('📦 API Response:', response.data);

    // Log the first item in detail to see all fields
if (response.data.length > 0) {
  console.log('📄 FIRST ITEM FIELDS:', Object.keys(response.data[0]));
  console.log('📄 FIRST ITEM:', response.data[0]);
  console.log('📄 signed_off_by in first item:', response.data[0].signed_off_by);
}
    
    // Handle different response formats
    let instructionsData = [];
    if (response.data && Array.isArray(response.data)) {
      instructionsData = response.data;
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      instructionsData = response.data.data;
    } else {
      instructionsData = [];
    }
    
    console.log('📊 Instructions data:', instructionsData);
    
        const mappedInstructions = instructionsData.map((inst: any) => ({
  id: inst.id,
  created_date: inst.created_at,
  reference_number: inst.reference_number || `INS-${inst.id}`,
  // Use consultant_name for the consultant field (from the consultants table)
  consultant_name: inst.consultant_name && inst.consultant_surname 
    ? `${inst.consultant_name} ${inst.consultant_surname}`
    : 'Unassigned',
  // Use employee_name for the employee field (from the employees table)
  employee_name: inst.employee_name && inst.employee_surname 
    ? `${inst.employee_name} ${inst.employee_surname}`
    : 'Unassigned',
  assigned_to: inst.assigned_to,
  consultant_id: inst.consultant_id,
  policy_id: inst.policy_id,
  policy_number: inst.policy_number || 'N/A',
  instruction_type: inst.instruction_type,
  instruction_method: inst.instruction_method,
  description: inst.description,
  status: inst.status,
  date_received: inst.date_received,
  date_sign_off: inst.date_sign_off,
  signed_off_by: inst.signed_off_by,
}));
    
    console.log('✅ Mapped instructions:', mappedInstructions);
    setInstructions(mappedInstructions);
    
  } catch (error) {
    console.error('Error fetching instructions:', error);
  } finally {
    setIsLoadingInstructions(false);
  }
};

const fetchInstructionDetail = async (instructionId: string) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');

    await fetchPolicies(clientId);
    await fetchEmployees();

    const response = await axios.get(
      `${API_URL}/api/clients_personal_instructions/${instructionId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const apiResponse = response.data;
    console.log('📄 Full API response:', apiResponse);
    
    const data = apiResponse.data || apiResponse;
    console.log('📄 Instruction detail data:', data);
    console.log('📄 consultant_id in detail:', data.consultant_id);
    console.log('📄 consultant_name in detail:', data.consultant_name);
    console.log('📄 consultant_surname in detail:', data.consultant_surname);
    console.log('📄 assigned_to in detail:', data.assigned_to);

    // Determine the display name
    let displayName = 'Unassigned';
    if (data.consultant_name && data.consultant_surname) {
      displayName = `${data.consultant_name} ${data.consultant_surname}`;
    } else if (data.employee_name && data.employee_surname) {
      displayName = `${data.employee_name} ${data.employee_surname}`;
    }

    setSelectedInstruction({
      id: data.id,
      created_date: data.created_at || data.created_date,
      reference_number: data.reference_number || `INS-${data.id}`,
      client_name: data.client_name ? `${data.client_name} ${data.client_surname || ''}` : 'Unknown Client',
      consultant_name: displayName,
      employee_name: data.employee_name && data.employee_surname 
        ? `${data.employee_name} ${data.employee_surname}`
        : 'Unassigned',  // ✅ ADD THIS
      assigned_to: data.assigned_to,
      consultant_id: data.consultant_id ? Number(data.consultant_id) : undefined,
      instruction_type: data.instruction_type,
      instruction_method: data.instruction_method || '',
      description: data.description,
      status: data.status,
      policy_number: data.policy_number || 'N/A',
      policy_id: data.policy_id,
      date_received: data.date_received || '',
      date_sign_off: data.date_sign_off || '',
      signed_off_by: data.signed_off_by || '',
      client_id: parseInt(clientId),
      originalDescription: data.description
    });

    setSignedOffByName(data.signed_off_by || '');
    
  } catch (error) {
    console.error('Error fetching instruction details:', error);
    setError('Failed to load instruction details');
  }
};

  const fetchClientDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
  `${API_URL}/api/clients/${clientId}`,
  { headers: { Authorization: `Bearer ${token}` } }
);
      
      const client = response.data;
      setSelectedClient(client);
      
      // Fetch policies for this client
      fetchPolicies(clientId);
    } catch (error) {
      console.error('Error fetching client details:', error);
    }
  };

      const fetchPolicies = async (clientId: string) => {
    setIsLoadingPolicies(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/instructions/policies?clientId=${clientId}&status=active`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('📋 Policies API response:', response.data);
      
      // Handle response format
      let policiesData = [];
      if (Array.isArray(response.data)) {
        policiesData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        policiesData = response.data.data;
      }
      
      setPolicies(policiesData);
      console.log('✅ Policies loaded:', policiesData.length);
      
    } catch (error) {
      console.error('Error fetching policies:', error);
      setPolicies([]);
    } finally {
      setIsLoadingPolicies(false);
    }
  };

      const fetchEmployees = async () => {
  console.log('🚀 FETCH EMPLOYEES CALLED!');
  setIsLoadingConsultants(true);
  setError('');
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('❌ No authentication token found');
      throw new Error('No authentication token found');
    }

    console.log('🔍 Fetching employees from:', `${API_URL}/api/employees`);
    
    const response = await axios.get(
      `${API_URL}/api/employees`,
      { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        } 
      }
    );
    
    console.log('📥 Full API response:', response);
    console.log('📥 Response data:', response.data);
    
    let employeesData: any[] = [];
    
    // Handle different response formats
    if (response.data && response.data.success && Array.isArray(response.data.data)) {
      employeesData = response.data.data;
      console.log('✅ Found employees in response.data.data:', employeesData.length);
    } else if (Array.isArray(response.data)) {
      employeesData = response.data;
      console.log('✅ Found employees in response.data (array):', employeesData.length);
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      employeesData = response.data.data;
      console.log('✅ Found employees in response.data.data (nested):', employeesData.length);
    } else {
      console.warn('⚠️ Unexpected response format:', response.data);
      employeesData = [];
    }
    
    console.log('📋 Raw employees data:', employeesData);
    
    // Filter active employees and ensure IDs are numbers
    const activeEmployees = employeesData
      .filter((c: any) => c.active !== false)
      .map((c: any) => ({
        id: Number(c.id),
        name: c.name || '',
        surname: c.surname || '',
        email: c.email || '',
        role: c.role || 'employee'
      }));
    
    console.log('✅ Active employees loaded:', activeEmployees.length);
    console.log('📋 Active employees:', activeEmployees);
    
    setEmployees(activeEmployees);
    
  } catch (error: any) {
    console.error('❌ Error fetching employees:', error);
    console.error('Error details:', error.response?.data);
    setEmployees([]);
    setError('Failed to load employees: ' + (error.response?.data?.message || error.message));
  } finally {
    setIsLoadingConsultants(false);
  }
};

// Fetch consultants (for the Instruction Details "Assign to Consultant" dropdown)
const fetchConsultants = async () => {
  console.log('🚀 FETCH CONSULTANTS CALLED!');
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('❌ No authentication token found');
      throw new Error('No authentication token found');
    }

    console.log('🔍 Fetching consultants from:', `${API_URL}/api/consultants`);
    
    const response = await axios.get(
      `${API_URL}/api/consultants`,
      { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        } 
      }
    );
    
    console.log('📥 Full API response:', response);
    console.log('📥 Response data:', response.data);
    
    let consultantsData: any[] = [];
    
    // Handle different response formats
    if (response.data && response.data.success && Array.isArray(response.data.data)) {
      consultantsData = response.data.data;
      console.log('✅ Found consultants in response.data.data:', consultantsData.length);
    } else if (Array.isArray(response.data)) {
      consultantsData = response.data;
      console.log('✅ Found consultants in response.data (array):', consultantsData.length);
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      consultantsData = response.data.data;
      console.log('✅ Found consultants in response.data.data (nested):', consultantsData.length);
    } else {
      console.warn('⚠️ Unexpected response format:', response.data);
      consultantsData = [];
    }
    
    console.log('📋 Raw consultants data:', consultantsData);
    
    // Filter active consultants and ensure IDs are numbers
    const activeConsultants = consultantsData
      .filter((c: any) => c.active !== false)
      .map((c: any) => ({
        id: Number(c.id),
        name: c.name || '',
        surname: c.surname || '',
        email: c.email || '',
        role: c.role || 'consultant'
      }));
    
    console.log('✅ Active consultants loaded:', activeConsultants.length);
    console.log('📋 Active consultants:', activeConsultants);
    
    setConsultants(activeConsultants);
    
  } catch (error: any) {
    console.error('❌ Error fetching consultants:', error);
    console.error('Error details:', error.response?.data);
    setConsultants([]);
  }
};

  const handleDescriptionUpdate = async () => {
    if (!selectedInstruction) return;
    
    setIsSavingInstructionType(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.put(
        `${API_URL}/api/instructions/${selectedInstruction.id}`,
        { description: selectedInstruction.description },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Instruction updated successfully' || response.status === 200) {
        setSuccess('Description updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchInstructions();
        
        // Store the new description as original
        setSelectedInstruction({
          ...selectedInstruction,
          originalDescription: selectedInstruction.description
        });
      }
    } catch (error: any) {
      console.error('Error updating description:', error);
      setError(error.response?.data?.message || 'Failed to update description');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSavingInstructionType(false);
    }
  };

    const handleOpenModal = () => {
  setFormData({
    policyId: '',
    instructionType: '',
    instructionMethod: '',
    description: '',
    assignedTo: ''
  });
  setError('');
  fetchConsultants();
  setIsModalOpen(true);
};

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setError('');
  };

      const handleReferenceClick = async (instructionId: string) => {
  setSelectedInstructionId(instructionId);
  
  // Fetch consultants for the detail view dropdown
  console.log('🔍 Fetching consultants before showing detail...');
  await fetchConsultants();
  
  const instruction = instructions.find(i => i.id.toString() === instructionId);
  console.log('🔍 Found instruction from list:', instruction);
  console.log('🔍 Policy ID from instruction:', instruction?.policy_id);
  console.log('🔍 Policy Number from instruction:', instruction?.policy_number);
  
  if (instruction) {
    setSelectedInstruction({
      ...instruction,
      instruction_method: instruction.instruction_method || '',
      date_received: instruction.date_received || '',
      date_sign_off: instruction.date_sign_off || '',
      policy_id: instruction.policy_id,
      client_id: parseInt(clientId),
      originalDescription: instruction.description,
      employee_name: instruction.employee_name || 'Unassigned',  // ✅ ADD THIS
      consultant_name: instruction.consultant_name || 'Unassigned'  // ✅ ADD THIS
    } as InstructionDetail & { originalDescription?: string });
    
    await fetchPolicies(clientId);
  }
  setView('detail');
};

  const handleBackToList = () => {
    setView('list');
    setSelectedInstructionId(null);
    setSelectedInstruction(null);
  };

  const handleInstructionTypeChange = async (newType: string) => {
  if (!selectedInstruction || selectedInstruction.instruction_type === newType) return;
  
  setIsSavingInstructionType(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const response = await axios.put(
      `${API_URL}/api/instructions/${selectedInstruction.id}`,
      { instruction_type: newType },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.message === 'Instruction updated successfully' || response.status === 200) {
      setSelectedInstruction({
        ...selectedInstruction,
        instruction_type: newType
      });
      setSuccess('Instruction type updated successfully');
      
      // --- ADD ACTIVITY LOG ---
      const typeLabel = instructionTypes.find(t => t.value === newType)?.label || newType;
      let performedBy = 'System';
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const name = user.username || user.email || 'System';
          const surname = user.surname || '';
          performedBy = `${name} ${surname}`.trim() || name || 'System';
        } catch (e) {
          performedBy = 'System';
        }
      }

      await axios.post(
        `${API_URL}/api/instruction-notes`,
        {
          instruction_id: selectedInstruction.id,
          action: 'type_changed',
          description: `Instruction type changed to: ${typeLabel}`,
          performed_by: performedBy
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // --- END ACTIVITY LOG ---
      
      setTimeout(() => setSuccess(''), 3000);
      fetchInstructions();
    }
  } catch (error: any) {
    console.error('Error updating instruction type:', error);
    setError(error.response?.data?.message || 'Failed to update instruction type');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSavingInstructionType(false);
  }
};

  const handlePolicyChange = async (newPolicyId: string) => {
  if (!selectedInstruction) return;
  
  setIsSavingInstructionType(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const policyId = newPolicyId ? parseInt(newPolicyId) : null;
    const selectedPolicy = policies.find(p => p.id === policyId);
    
    const response = await axios.put(
      `${API_URL}/api/instructions/${selectedInstruction.id}`,
      { 
        policy_id: policyId,
        instruction_type: selectedInstruction.instruction_type
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.message === 'Instruction updated successfully' || response.status === 200) {
      setSelectedInstruction({
        ...selectedInstruction,
        policy_id: policyId || undefined,
        policy_number: selectedPolicy?.policy_number || 'No policy selected'
      });
      setSuccess('Policy updated successfully');
      
      // --- ADD ACTIVITY LOG ---
      let performedBy = 'System';
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const name = user.username || user.email || 'System';
          const surname = user.surname || '';
          performedBy = `${name} ${surname}`.trim() || name || 'System';
        } catch (e) {
          performedBy = 'System';
        }
      }

      await axios.post(
        `${API_URL}/api/instruction-notes`,
        {
          instruction_id: selectedInstruction.id,
          action: 'policy_updated',
          description: `Policy updated to: ${selectedPolicy?.policy_number || 'No policy'}`,
          performed_by: performedBy
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // --- END ACTIVITY LOG ---
      
      setTimeout(() => setSuccess(''), 3000);
      fetchInstructions();
    }
  } catch (error: any) {
    console.error('Error updating policy:', error);
    setError(error.response?.data?.message || 'Failed to update policy');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSavingInstructionType(false);
  }
};

  const handleStatusChange = async (newStatus: string) => {
  if (!selectedInstruction || selectedInstruction.status === newStatus) return;
  
  setIsSavingInstructionType(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const response = await axios.put(
      `${API_URL}/api/instructions/${selectedInstruction.id}`,
      { status: newStatus },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.message === 'Instruction updated successfully' || response.status === 200) {
      setSelectedInstruction({
        ...selectedInstruction,
        status: newStatus
      });
      setSuccess('Instruction status updated successfully');
      
      // --- ADD ACTIVITY LOG ---
      const statusLabel = instructionStatuses.find(s => s.value === newStatus)?.label || newStatus;
      let performedBy = 'System';
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const name = user.username || user.email || 'System';
          const surname = user.surname || '';
          performedBy = `${name} ${surname}`.trim() || name || 'System';
        } catch (e) {
          performedBy = 'System';
        }
      }

      await axios.post(
        `${API_URL}/api/instruction-notes`,
        {
          instruction_id: selectedInstruction.id,
          action: 'status_changed',
          description: `Status changed to: ${statusLabel}`,
          performed_by: performedBy
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // --- END ACTIVITY LOG ---
      
      setTimeout(() => setSuccess(''), 3000);
      fetchInstructions();
    }
  } catch (error: any) {
    console.error('Error updating status:', error);
    setError(error.response?.data?.message || 'Failed to update instruction status');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSavingInstructionType(false);
  }
};

  const handleMethodChange = async (newMethod: string) => {
  if (!selectedInstruction || selectedInstruction.instruction_method === newMethod) return;
  
  setIsSavingInstructionType(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const response = await axios.put(
      `${API_URL}/api/instructions/${selectedInstruction.id}`,
      { instruction_method: newMethod },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.message === 'Instruction updated successfully' || response.status === 200) {
      setSelectedInstruction({
        ...selectedInstruction,
        instruction_method: newMethod
      });
      setSuccess('Method updated successfully');
      
      // --- ADD ACTIVITY LOG ---
      const methodLabel = instructionMethods.find(m => m.value === newMethod)?.label || newMethod;
      let performedBy = 'System';
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const name = user.username || user.email || 'System';
          const surname = user.surname || '';
          performedBy = `${name} ${surname}`.trim() || name || 'System';
        } catch (e) {
          performedBy = 'System';
        }
      }

      await axios.post(
        `${API_URL}/api/instruction-notes`,
        {
          instruction_id: selectedInstruction.id,
          action: 'method_changed',
          description: `Method changed to: ${methodLabel}`,
          performed_by: performedBy
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // --- END ACTIVITY LOG ---
      
      setTimeout(() => setSuccess(''), 3000);
      fetchInstructions();
    }
  } catch (error: any) {
    console.error('Error updating method:', error);
    setError(error.response?.data?.message || 'Failed to update method');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSavingInstructionType(false);
  }
};

const handleConsultantAssignment = async (consultantId: string) => {
  if (!selectedInstruction) return;
  
  setIsSavingInstructionType(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const consultantIdValue = consultantId ? parseInt(consultantId) : null;
    
    console.log('📝 Assigning consultant:', { 
      instructionId: selectedInstruction.id, 
      consultantIdValue 
    });
    
    const response = await axios.put(
      `${API_URL}/api/instructions/${selectedInstruction.id}`,
      { consultant_id: consultantIdValue },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    console.log('✅ Consultant assignment response:', response.data);
    
    if (response.data.message === 'Instruction updated successfully' || response.status === 200) {
      const consultant = consultants.find(c => c.id === consultantIdValue);
      const consultantName = consultant ? `${consultant.name} ${consultant.surname}` : 'Unassigned';
      
      // Update the selected instruction with the new consultant
      setSelectedInstruction({
        ...selectedInstruction,
        consultant_id: consultantIdValue || undefined,
        consultant_name: consultantName
      });
      
      setSuccess('Consultant assigned successfully');
      
      // Refresh the instructions list
      await fetchInstructions();
      
      // Reset the selected instruction to show the updated data
      if (selectedInstruction.id) {
        await fetchInstructionDetail(selectedInstruction.id.toString());
      }
      
      setTimeout(() => setSuccess(''), 3000);
    }
  } catch (error: any) {
    console.error('❌ Error assigning consultant:', error);
    console.error('❌ Error response:', error.response?.data);
    setError(error.response?.data?.message || 'Failed to assign consultant');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSavingInstructionType(false);
  }
};

  const handleDateReceivedChange = async (newDate: string) => {
    if (!selectedInstruction) return;
    
    setIsSavingInstructionType(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      // Change from PATCH to PUT
      const response = await axios.put(
        `${API_URL}/api/instructions/${selectedInstruction.id}`,
        { date_received: newDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Instruction updated successfully' || response.status === 200) {
        setSelectedInstruction({
          ...selectedInstruction,
          date_received: newDate
        });
        setSuccess('Date received updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchInstructions();
      }
    } catch (error: any) {
      console.error('Error updating date received:', error);
      setError(error.response?.data?.message || 'Failed to update date received');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSavingInstructionType(false);
    }
  };

    const handleSignedOff = async () => {
  if (!selectedInstruction) return;
  
  setIsSavingInstructionType(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const today = new Date().toISOString().split('T')[0];
    
    const storedUser = localStorage.getItem('user');
    let signedOffBy = '';
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const name = user.username || user.email || 'System';
        const surname = user.surname || '';
        signedOffBy = `${name} ${surname}`.trim() || name || 'System';
      } catch (e) {
        signedOffBy = 'System';
      }
    }
    
    const payload = { 
      date_sign_off: today,
      status: 'completed',
      signed_off_by: signedOffBy
    };
    
    console.log('📤 SIGNING OFF - PAYLOAD:', payload);
    console.log('📤 SIGNING OFF - URL:', `${API_URL}/api/instructions/${selectedInstruction.id}`);
    console.log('📤 SIGNING OFF - TOKEN:', token ? 'Exists' : 'Missing');
    
    const response = await axios.put(
      `${API_URL}/api/clients_personal_instructions/${selectedInstruction.id}`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    console.log('📤 SIGN OFF RESPONSE:', response.data);
    console.log('📤 SIGN OFF RESPONSE DATA:', response.data.data);
    
    if (response.data.success || response.status === 200) {
      setSuccess('Instruction signed off successfully');
      
      // Navigate back to list view after successful sign-off
      setView('list');
      setSelectedInstructionId(null);
      setSelectedInstruction(null);
      
      // Refresh the instructions list
      await fetchInstructions();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    }
  } catch (error: any) {
    console.error('Error signing off instruction:', error);
    console.error('Error response:', error.response?.data);
    setError(error.response?.data?.message || 'Failed to sign off instruction');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSavingInstructionType(false);
  }
};

  const handleUpdateInstructionType = async () => {
    if (!selectedInstruction || !editingInstructionType) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.patch(
        `${API_URL}/api/instructions/${selectedInstruction.id}`,
        { instruction_type: editingInstructionType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success || response.status === 200) {
        // Update local state
        setSelectedInstruction({
          ...selectedInstruction,
          instruction_type: editingInstructionType
        });
        setIsEditingInstructionType(false);
        setSuccess('Instruction type updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        // Refresh the list
        fetchInstructions();
      }
    } catch (error: any) {
      console.error('Error updating instruction type:', error);
      setError(error.response?.data?.message || 'Failed to update instruction type');
    } finally {
      setIsLoading(false);
    }
  };

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  
  if (!formData.instructionType || !formData.instructionMethod || !formData.description) {
    setError('Please fill in all required fields');
    return;
  }

  setIsLoading(true);
  setError('');

  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');

    // Convert assignedTo to number only if it has a value
    let assignedToId = null;
    if (formData.assignedTo && formData.assignedTo !== '') {
      const parsedId = parseInt(formData.assignedTo);
      if (!isNaN(parsedId) && parsedId > 0) {
        assignedToId = parsedId;
      }
    }

    const payload = {
      client_id: parseInt(clientId),
      policy_id: formData.policyId ? parseInt(formData.policyId) : null,
      instruction_type: formData.instructionType,
      instruction_method: formData.instructionMethod,
      description: formData.description,
            consultant_id: assignedToId,
      status: 'pending',
      date_received: new Date().toISOString().split('T')[0]
    };

    console.log('📤 Creating instruction with payload:', payload);

    const response = await axios.post(
      `${API_URL}/api/instructions`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setSuccess('Instruction created successfully!');
    
    if (response.data.success && response.data.data) {
      const instruction = response.data.data;
      
      let performedBy = 'System';
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const name = user.username || user.email || 'System';
          const surname = user.surname || '';
          performedBy = `${name} ${surname}`.trim() || name || 'System';
        } catch (e) {
          performedBy = 'System';
        }
      }

      await axios.post(
        `${API_URL}/api/instruction-notes`,
        {
          instruction_id: instruction.id,
          action: 'created',
          description: `Instruction created: ${formData.instructionType.replace('_', ' ')} - ${formData.description.substring(0, 50)}${formData.description.length > 50 ? '...' : ''}`,
          performed_by: performedBy
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }
    
    handleCloseModal();
    
    setFormData({
      policyId: '',
      instructionType: '',
      instructionMethod: '',
      description: '',
      assignedTo: ''
    });
    
    await fetchInstructions();
    
    setTimeout(() => setSuccess(''), 3000);
    
  } catch (error: any) {
    console.error('Error creating instruction:', error);
    console.error('Error response:', error.response?.data);
    setError(error.response?.data?.message || 'Failed to create instruction');
  } finally {
    setIsLoading(false);
  }
};

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatLabel = (value: string): string => {
    return value.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

      interface InstructionSectionProps {
    title: string;
    instructions: Instruction[];
    onReferenceClick: (id: string) => void;
    formatDate: (date: string) => string;
    formatLabel: (value: string) => string;
  }

  // Instruction Section Component
const InstructionSection: React.FC<InstructionSectionProps> = ({ 
  title, 
  instructions, 
  onReferenceClick, 
  formatDate, 
  formatLabel 
}) => {
  if (instructions.length === 0) {
    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-3 text-gray-800">{title} ({instructions.length})</h3>
        <p className="text-gray-500 italic">No {title.toLowerCase()}</p>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold mb-3 text-gray-800">{title} ({instructions.length})</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Instruction No</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Description</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Consultant</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Created</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Last Updated</th>
              <th className="px-4 py-2 text-center text-sm font-medium text-gray-600 border-b">Status</th>
            </tr>
          </thead>
          <tbody>
            {instructions.map((instruction) => (
              <tr key={instruction.id} className="hover:bg-gray-50 border-b border-gray-100">
                <td className="px-4 py-2 text-sm">
                  <button
                    onClick={() => onReferenceClick(instruction.id.toString())}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    {instruction.reference_number}
                  </button>
                </td>
                <td className="px-4 py-2 text-sm text-gray-800 max-w-xs truncate">
                  {instruction.description}
                </td>
                                <td className="px-4 py-2 text-sm text-gray-600">
                  {instruction.consultant_name}
                </td>
                <td className="px-4 py-2 text-sm text-gray-600">
                  {formatDate(instruction.created_date)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-600">
                  {formatDate(instruction.date_sign_off || instruction.created_date)}
                </td>
                {/* Status Column */}
                <td className="px-4 py-2 text-sm text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    instruction.status?.toLowerCase() === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : instruction.status?.toLowerCase() === 'cancelled'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {instruction.status === 'pending' ? 'Open' : formatLabel(instruction.status || 'Open')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

    // Detail View
  if (view === 'detail' && selectedInstruction) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Instruction Details</h3>
          <div className="flex gap-2">
            {selectedInstruction.status?.toLowerCase() === 'completed' && !selectedInstruction.date_sign_off && (
              <button
                onClick={handleSignedOff}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Signed Off
              </button>
            )}
            <button
              onClick={handleBackToList}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back to List
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Instruction Details
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'documents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Documents
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'notes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Notes
            </button>
            <button
              onClick={() => setActiveTab('whatsapp')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'whatsapp'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              WhatsApp
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'email'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Email
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-4">
          {activeTab === 'details' && (
            <div className="bg-white shadow-md rounded-lg p-6 border">
              <div className="grid grid-cols-2 gap-6">
                {/* Reference Number */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
  <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 border border-gray-200 rounded-md">
    {selectedInstruction.reference_number}
  </div>
</div>

{/* Instruction Type - Always a dropdown */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Instruction Type</label>
  <select
    value={selectedInstruction.instruction_type}
    onChange={(e) => handleInstructionTypeChange(e.target.value)}
    disabled={isSavingInstructionType}
    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
  >
    <option value="new_product">New Product</option>
    <option value="update">Update</option>
    <option value="cancellation">Cancellation</option>
  </select>
  {isSavingInstructionType && (
    <p className="text-xs text-blue-600 mt-1">Saving...</p>
  )}
</div>

{/* Assign to Consultant */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Consultant</label>
  <select
    value={selectedInstruction.consultant_id?.toString() || ''}
    onChange={(e) => handleConsultantAssignment(e.target.value)}
    disabled={isSavingInstructionType || isLoadingConsultants}
    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
  >
    <option value="">Unassigned</option>
    {consultants.map((consultant) => (
      <option key={consultant.id} value={consultant.id.toString()}>
        {consultant.name} {consultant.surname}
      </option>
    ))}
  </select>
  {isLoadingConsultants && (
    <p className="text-xs text-blue-600 mt-1">Loading consultants...</p>
  )}
  {isSavingInstructionType && (
    <p className="text-xs text-blue-600 mt-1">Saving...</p>
  )}
</div>

                                {/* Description - Editable */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <div className="flex gap-2">
                    <textarea
                      value={selectedInstruction.description}
                      onChange={(e) => {
                        setSelectedInstruction({
                          ...selectedInstruction,
                          description: e.target.value
                        });
                      }}
                      disabled={isSavingInstructionType}
                      rows={3}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Enter description..."
                    />
                    {selectedInstruction.description !== selectedInstruction.originalDescription && (
                      <button
                        onClick={handleDescriptionUpdate}
                        disabled={isSavingInstructionType}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Save
                      </button>
                    )}
                  </div>
                  {isSavingInstructionType && (
                    <p className="text-xs text-blue-600 mt-1">Saving...</p>
                  )}
                </div>

                {/* Method Received - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Method Received</label>
                  <select
                    value={selectedInstruction.instruction_method || ''}
                    onChange={(e) => handleMethodChange(e.target.value)}
                    disabled={isSavingInstructionType}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select method</option>
                    {instructionMethods.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                  {isSavingInstructionType && (
                    <p className="text-xs text-blue-600 mt-1">Saving...</p>
                  )}
                </div>

                                {/* Policy Number - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Policy Number</label>
                  <select
                    value={selectedInstruction.policy_id ? selectedInstruction.policy_id.toString() : ''}
                    onChange={(e) => handlePolicyChange(e.target.value)}
                    disabled={isSavingInstructionType || isLoadingPolicies}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">No policy selected</option>
                    {policies.map((policy) => (
                      <option key={policy.id} value={policy.id.toString()}>
                        {policy.policy_number}
                      </option>
                    ))}
                  </select>
                  {isLoadingPolicies && (
                    <p className="text-xs text-blue-600 mt-1">Loading policies...</p>
                  )}
                  {policies.length === 0 && !isLoadingPolicies && (
                    <p className="text-xs text-gray-500 mt-1">No active policies available for this client</p>
                  )}
                </div>

                {/* Instruction Status - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instruction Status</label>
                  <select
                    value={selectedInstruction.status?.toLowerCase() || ''}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={isSavingInstructionType}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {instructionStatuses.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  {isSavingInstructionType && (
                    <p className="text-xs text-blue-600 mt-1">Saving...</p>
                  )}
                </div>

                {/* Date Received - Date Picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Received</label>
                  <input
                    type="date"
                    value={selectedInstruction.date_received?.split('T')[0] || selectedInstruction.created_date?.split('T')[0] || ''}
                    onChange={(e) => handleDateReceivedChange(e.target.value)}
                    disabled={isSavingInstructionType}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  {isSavingInstructionType && (
                    <p className="text-xs text-blue-600 mt-1">Saving...</p>
                  )}
                </div>

                                {/* Date Sign Off and Signed Off By - Only show if signed off */}
                {selectedInstruction.date_sign_off && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date Sign Off</label>
                      <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 border border-gray-200 rounded-md">
                        {formatDate(selectedInstruction.date_sign_off)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Signed Off By</label>
                      <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 border border-gray-200 rounded-md">
                        {selectedInstruction.signed_off_by || signedOffByName || 'System'}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Instruction Questions Section */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <InstructionQuestions 
                  instructionId={selectedInstruction.id.toString()} 
                  clientId={clientId}
                  clientName={selectedClient?.name + ' ' + selectedClient?.surname || 'Client'}
                />
              </div>
            </div>
          )}

                    {activeTab === 'documents' && (
  <div className="bg-white shadow-md rounded-lg p-6 border">
    <ClientInstructionDocuments 
      instructionId={selectedInstruction.id.toString()} 
      clientId={clientId}
      onDocumentChange={refreshNotes}
    />
  </div>
)}

                    {activeTab === 'notes' && selectedInstruction && (
  <div className="bg-white shadow-md rounded-lg p-6 border">
    <ClientInstructionNotes 
      instructionId={selectedInstruction.id.toString()} 
    />
  </div>
)}

          {activeTab === 'whatsapp' && (
  <div className="bg-white shadow-md rounded-lg p-6 border">
    <ClientInstructionWhatsAppTab 
      clientId={clientId}
      instructionId={selectedInstruction?.id?.toString() || ''}
      instructionRef={selectedInstruction?.reference_number || ''}
      clientName={selectedClient ? `${selectedClient.name} ${selectedClient.surname}` : ''}
      policyNumber={selectedInstruction?.policy_number || ''}
      user={JSON.parse(localStorage.getItem('user') || '{}')}
    />
  </div>
)}

          {activeTab === 'email' && (
  <div className="bg-white shadow-md rounded-lg p-6 border">
    <ClientInstructionEmailTab 
      clientId={clientId}
      instructionId={selectedInstruction?.id?.toString() || ''}
      instructionRef={selectedInstruction?.reference_number || ''}
      clientName={selectedClient ? `${selectedClient.name} ${selectedClient.surname}` : ''}
      policyNumber={selectedInstruction?.policy_number || ''}
      user={JSON.parse(localStorage.getItem('user') || '{}')}
    />
  </div>
)}
        </div>
      </div>
    );
  }

    // List View
    const openInstructions = instructions.filter(i => i.status?.toLowerCase() !== 'completed');
    const completedInstructions = instructions.filter(i => i.status?.toLowerCase() === 'completed');

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold text-gray-800">Client Instructions</h3>
            <p className="text-sm text-gray-500 mt-1">Manage client instructions and track progress</p>
          </div>
          <button
            onClick={handleOpenModal}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Create Instruction
          </button>
        </div>

        {/* Error and Success Messages */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
            <button onClick={() => setError('')} className="float-right text-red-700 hover:text-red-900">×</button>
          </div>
        )}
        {success && (
          <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        {/* Loading State */}
        {isLoadingInstructions ? (
          <div className="text-center py-8 text-gray-500">Loading instructions...</div>
        ) : (
          <>
            {/* Open Instructions Section */}
            <InstructionSection 
              title="Open Instructions" 
              instructions={openInstructions}
              onReferenceClick={handleReferenceClick}
              formatDate={formatDate}
              formatLabel={formatLabel}
            />

            {/* Completed Instructions Section */}
            <InstructionSection 
              title="Completed Instructions" 
              instructions={completedInstructions}
              onReferenceClick={handleReferenceClick}
              formatDate={formatDate}
              formatLabel={formatLabel}
            />
          </>
        )}

        

      {/* Create Instruction Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal}></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Create New Instruction
                      </h3>
                      
                      {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                          {error}
                        </div>
                      )}

                      <div className="space-y-4">

                        {/* Select Policy */}
                        <div>
                          <label htmlFor="policy" className="block text-sm font-medium text-gray-700 mb-1">
                            Select Policy <span className="text-gray-400 text-xs">(optional)</span>
                          </label>
                          <select
                            id="policy"
                            value={formData.policyId}
                            onChange={(e) => setFormData(prev => ({ ...prev, policyId: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={isLoadingPolicies}
                          >
                            <option value="">No policy selected</option>
                            {policies.map(policy => (
                              <option key={policy.id} value={policy.id}>
                                {policy.policy_number}
                              </option>
                            ))}
                          </select>
                          {isLoadingPolicies && (
                            <p className="text-xs text-blue-600 mt-1">Loading policies...</p>
                          )}
                          {policies.length === 0 && !isLoadingPolicies && (
                            <p className="text-xs text-gray-500 mt-1">No active policies found for this client</p>
                          )}
                        </div>

                        {/* Select Instruction Type */}
                        <div>
                          <label htmlFor="instructionType" className="block text-sm font-medium text-gray-700 mb-1">
                            Select Instruction Type *
                          </label>
                          <select
                            id="instructionType"
                            value={formData.instructionType}
                            onChange={(e) => setFormData(prev => ({ ...prev, instructionType: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          >
                            <option value="">Select instruction type</option>
                            {instructionTypes.map(type => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Select Instruction Method */}
                        <div>
                          <label htmlFor="instructionMethod" className="block text-sm font-medium text-gray-700 mb-1">
                            Select Instruction Method *
                          </label>
                          <select
                            id="instructionMethod"
                            value={formData.instructionMethod}
                            onChange={(e) => setFormData(prev => ({ ...prev, instructionMethod: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          >
                            <option value="">Select method received</option>
                            {instructionMethods.map(method => (
                              <option key={method.value} value={method.value}>
                                {method.label}
                              </option>
                            ))}
                          </select>
                        </div>

                                                {/* Select Consultant */}
<div>
  <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
    Select Consultant <span className="text-gray-400 text-xs">(optional)</span>
  </label>
  <select
    id="assignedTo"
    value={formData.assignedTo}
    onChange={(e) => setFormData(prev => ({ ...prev, assignedTo: e.target.value }))}
    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    disabled={isLoadingConsultants}
  >
    <option value="">Select consultant</option>
    {consultants.map((consultant: Consultant) => (
      <option key={consultant.id} value={String(consultant.id)}>
        {consultant.name} {consultant.surname}
      </option>
    ))}
  </select>
  {isLoadingConsultants && (
    <p className="text-xs text-blue-600 mt-1">Loading consultants...</p>
  )}
  {consultants.length === 0 && !isLoadingConsultants && (
    <p className="text-xs text-gray-500 mt-1">No consultants available</p>
  )}
</div>

                        {/* Description */}
                        <div>
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            Description *
                          </label>
                          <textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter instruction description..."
                            required
                          />
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Modal footer with buttons */}
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Creating...' : 'Submit'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Instruction Questions Component
interface InstructionQuestionsProps {
  instructionId: string;
  clientId: string;
  clientName: string;
}

interface Question {
  id: number;
  question: string;
  answer?: boolean | null;
  is_required: boolean;
  order: number;
}

interface Answer {
  question_id: number;
  answer: boolean;
  created_at?: string;
}

interface Employee {
  id: number;
  name: string;
  surname: string;
  email: string;
  id_passport_number?: string;
  contact_number?: string;
  active?: boolean;
  re_compliant?: boolean;
  work_model?: string;
  position?: string;
  roles?: string[];
}

const InstructionQuestions: React.FC<InstructionQuestionsProps> = ({ instructionId, clientId, clientName }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<number, boolean>>({});
  const [answerTimestamps, setAnswerTimestamps] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loggedInUserName, setLoggedInUserName] = useState<string>('');

    // Get logged-in user name when component mounts
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    console.log('📦 Stored user data:', storedUser);
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        console.log('👤 Parsed user:', user);
        console.log('🔍 All user keys:', Object.keys(user));
        
        // Use username and surname together
        const name = user.username || user.email || 'User';
        const surname = user.surname || '';
        const fullName = `${name} ${surname}`.trim();
        
        console.log('✅ Name:', name, 'Surname:', surname);
        console.log('✅ Full name:', fullName);
        setLoggedInUserName(fullName || name || 'User');
      } catch (e) {
        console.error('Error parsing user:', e);
        setLoggedInUserName('User');
      }
    }
  }, []);

  // Fetch questions and existing answers
  useEffect(() => {
    fetchQuestions();
  }, [instructionId]);

const fetchQuestions = async () => {
  setIsLoading(true);
  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || '';

    // Fetch declaration questions
    const questionsResponse = await fetch(`${API_URL}/api/instruction-questions`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!questionsResponse.ok) {
      throw new Error('Failed to fetch questions');
    }

    const questionsData = await questionsResponse.json();
    
    // Handle different response formats
    let questionsList: Question[] = [];
    if (questionsData && questionsData.success && Array.isArray(questionsData.data)) {
      questionsList = questionsData.data;
    } else if (Array.isArray(questionsData)) {
      questionsList = questionsData;
    }

    // Fetch existing answers for this instruction
    const answersResponse = await fetch(`${API_URL}/api/instruction-questions/answers/${instructionId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    let existingAnswers: Record<number, boolean> = {};
    let existingTimestamps: Record<number, string> = {};
    
    if (answersResponse.ok) {
      const answersData = await answersResponse.json();
      
      if (answersData && answersData.success && Array.isArray(answersData.data)) {
        answersData.data.forEach((answer: Answer) => {
          existingAnswers[answer.question_id] = answer.answer;
          if (answer.created_at) {
            existingTimestamps[answer.question_id] = answer.created_at;
          }
        });
      }
    }
    
    // CRITICAL FIX: Initialize answers for all questions
    // Questions without existing answers will be undefined, which is fine
    // But we need to make sure the questions list is set first
    setQuestions(questionsList);
    setAnswers(existingAnswers);
    setAnswerTimestamps(existingTimestamps);
    
    console.log('Questions loaded:', questionsList);
    console.log('Existing answers:', existingAnswers);
    console.log('Answer timestamps:', existingTimestamps);
    
  } catch (error) {
    console.error('Error fetching questions:', error);
    setError('Failed to load questions');
  } finally {
    setIsLoading(false);
  }
};

  const handleAnswerChange = (questionId: number, value: boolean) => {
  console.log('Answer change:', { questionId, value });
  console.log('Current answers:', answers);
  console.log('Is answered?', answerTimestamps[questionId] !== undefined);
  
  // Only block if there's a timestamp (meaning it was saved before)
  // But allow changes if it's a new unsaved answer
  if (answerTimestamps[questionId] === undefined) {
    console.log('Allowing change - updating answer');
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  } else {
    console.log('Blocking change - question already saved');
  }
};

  const handleSaveAnswers = async () => {
  setIsSaving(true);
  setError('');
  setSuccess('');

  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || '';

    // Get logged-in user
    let signedOffBy = '';
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        signedOffBy = `${user.name || ''} ${user.surname || ''}`.trim() || user.username || user.email || 'User';
      } catch (e) {
        signedOffBy = 'User';
      }
    }

    const answersToSave = Object.entries(answers).map(([questionId, answer]) => ({
      instruction_id: parseInt(instructionId),
      question_id: parseInt(questionId),
      answer: answer,
      answered_by: signedOffBy
    }));

    const response = await fetch(`${API_URL}/api/instruction-questions/answers`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ answers: answersToSave }),
    });

    if (!response.ok) {
      throw new Error('Failed to save answers');
    }

    await fetchQuestions();
    setSuccess('Answers saved successfully!');
    setTimeout(() => setSuccess(''), 3000);
  } catch (error: any) {
    console.error('Error saving answers:', error);
    setError(error.message || 'Failed to save answers');
  } finally {
    setIsSaving(false);
  }
};

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6 border">
        <div className="text-center text-gray-500">Loading questions...</div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6 border">
        <h4 className="text-md font-semibold text-gray-900 mb-2">Instruction Questions</h4>
        <p className="text-sm text-gray-500">No questions configured for this instruction type.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6 border">
      <h4 className="text-md font-semibold text-gray-900 mb-4">Instruction Questions</h4>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm">
          {success}
        </div>
      )}

      <div className="space-y-4">
        {questions.map((question) => {
          const hasAnswer = answers[question.id] !== undefined;
          const isAnswered = answerTimestamps[question.id] !== undefined;
          
          return (
            <div 
              key={question.id} 
              className={`p-4 rounded-lg border transition-colors ${
                isAnswered 
                  ? 'bg-gray-100 border-gray-300' 
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <p className={`text-sm mb-3 ${isAnswered ? 'text-gray-500' : 'text-gray-900'}`}>
                {question.question}
              </p>
              
              <div className="flex space-x-6">
                {/* Yes option */}
                <label className={`flex items-center space-x-2 ${isAnswered ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={answers[question.id] === true}
                    onChange={() => handleAnswerChange(question.id, true)}
                    disabled={isAnswered}
                    className={`w-4 h-4 ${
                      isAnswered 
                        ? 'text-gray-400 border-gray-300 cursor-not-allowed' 
                        : 'text-blue-600 border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <span className={`text-sm ${isAnswered ? 'text-gray-400' : 'text-gray-700'}`}>
                    Yes
                  </span>
                </label>
                
                {/* No option */}
                <label className={`flex items-center space-x-2 ${isAnswered ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={answers[question.id] === false}
                    onChange={() => handleAnswerChange(question.id, false)}
                    disabled={isAnswered}
                    className={`w-4 h-4 ${
                      isAnswered 
                        ? 'text-gray-400 border-gray-300 cursor-not-allowed' 
                        : 'text-blue-600 border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <span className={`text-sm ${isAnswered ? 'text-gray-400' : 'text-gray-700'}`}>
                    No
                  </span>
                </label>
              </div>

              {/* Show stamp if question has been answered */}
              {isAnswered && answerTimestamps[question.id] && (
                <div className="mt-3 flex items-center">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600 border border-gray-300">
                    <svg className="w-3 h-3 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    <span className="mr-1 font-semibold">{loggedInUserName || 'User'}</span>
                    <span>•</span>
                    <span className="ml-1">{formatDateTime(answerTimestamps[question.id])}</span>
                  </div>
                </div>
              )}

            </div>
          );
        })}
      </div>

      {/* Only show Save button if there are unsaved answers */}
      {Object.keys(answers).length > Object.keys(answerTimestamps).length && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSaveAnswers}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Answers'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientPersonalInstructionTab;